import type { TFunction } from 'i18next';
import i18n from '../i18n/i18n';
import { buildTutorialSteps } from '../tutorial/buildSteps';
import { TUTORIAL_STEP_LAYOUT } from '../tutorial/stepLayout';
import type { TutorialStep } from '../tutorial/steps';

function layoutIndex(id: number): number {
  return TUTORIAL_STEP_LAYOUT.findIndex((s) => s.id === id);
}

/** Platform step IDs (not array slots) included in each training module tour. */
const MODULE_STEP_INDICES: Record<string, number[]> = {
  survey_connections: [1, 2].map(layoutIndex),
  ugridnet_design: [3, 4, 7].map(layoutIndex),
  construction_tracking: [5, 24].map(layoutIndex),
  plant_sizing: [8, 9, 10, 11, 23].map(layoutIndex),
  voltage_drop: [17, 18, 19, 20, 21].map(layoutIndex),
  topology_compare: [13, 15].map(layoutIndex),
};

/** Module tours that should create/load the disposable sandbox project. */
export const MODULES_USING_SANDBOX = new Set([
  'survey_connections',
  'ugridnet_design',
  'construction_tracking',
  'plant_sizing',
]);

export const MODULE_TOUR_IDS: Record<string, string> = {
  survey_connections: 'training_survey',
  ugridnet_design: 'training_ugridnet',
  construction_tracking: 'training_construction',
  load_8760: '8760_panel',
  plant_sizing: 'training_plant',
  voltage_drop: 'training_vdrop',
  topology_compare: 'training_topology',
  mechanical_checklist: 'mechanical_checklist',
  commissioning_checklist: 'commissioning_checklist',
  generation_layout: 'training_generation_layout',
  generation_capacity: 'training_generation_capacity',
};

/** Modules that launch a dedicated panel tour instead of the platform overlay. */
export const STANDALONE_TOUR_MODULES = new Set([
  'load_8760',
  'mechanical_checklist',
  'commissioning_checklist',
  'generation_layout',
  'generation_capacity',
]);

export function getModuleSteps(moduleId: string, t?: TFunction): TutorialStep[] {
  const indices = MODULE_STEP_INDICES[moduleId];
  if (!indices?.length) return [];
  const steps = buildTutorialSteps(t || i18n.t.bind(i18n));
  return indices.filter((i) => i >= 0).map((i) => steps[i]).filter(Boolean);
}

export function getModuleTourId(moduleId: string): string {
  return MODULE_TOUR_IDS[moduleId] || '';
}

export function moduleUsesPlatformOverlay(moduleId: string): boolean {
  return getModuleSteps(moduleId).length > 0;
}
